import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import AuthPage from './components/auth/AuthPage';
import Dashboard from './components/Dashboard';
import LoanCalculatorPage from './components/calculator/LoanCalculatorPage';
import { LoanApplicationFlow, ApplicationSuccess } from './components/application';
import { AdminDashboard } from './components/admin';
import StudentPayments from './components/StudentPayments';
import PayFastPayment from './components/payment/PayFastPayment';
import PaymentSuccess from './components/payment/PaymentSuccess';
import PaymentCancel from './components/payment/PaymentCancel';
import DebitOrderSetup from './components/payment/DebitOrderSetup';
import ProfilePage from './components/dashboard/ProfilePage';
import SupportPage from './components/dashboard/SupportPage';
import StudentNotifications from './components/dashboard/StudentNotifications';
import StudentShell from './components/dashboard/StudentShell';
import FAQPage from './components/faq/FAQPage';
import TermsPage from './components/legal/TermsPage';
import PrivacyPage from './components/legal/PrivacyPage';
import ErrorBoundary from './components/ErrorBoundary';
import './App.css';

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        background: 'linear-gradient(135deg, #0074D9 0%, #1E3A70 100%)'
      }}>
        <div style={{ 
          background: 'white', 
          padding: '40px', 
          borderRadius: '12px',
          textAlign: 'center',
          boxShadow: '0 20px 40px rgba(30, 58, 112, 0.15)'
        }}>
          <img src="/genlend-logo.png" alt="GenLend" style={{ width: '74px', height: '74px', borderRadius: '50%', marginBottom: '16px' }} />
          <div style={{ 
            width: '40px', 
            height: '40px', 
            border: '4px solid #E2E8F0',
            borderTop: '4px solid #0074D9',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 20px'
          }}></div>
          <h2 style={{ margin: 0, color: '#1E3A70', fontFamily: "'Montserrat', sans-serif" }}>Loading GenLend...</h2>
        </div>
      </div>
    );
  }
  
  return isAuthenticated ? <>{children}</> : <Navigate to="/auth" replace />;
};

function App() {
  return (
    <ErrorBoundary>
    <AuthProvider>
      <Router>
        <div className="App">
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<LoanCalculatorPage />} />
            <Route path="/auth" element={<AuthPage />} />
            <Route path="/faq" element={<FAQPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            
            {/* Application routes (protected) */}
            <Route 
              path="/apply" 
              element={
                <ProtectedRoute>
                  <LoanApplicationFlow />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/application/success" 
              element={
                <ProtectedRoute>
                  <ApplicationSuccess />
                </ProtectedRoute>
              } 
            />
            
            {/* Protected student app shell */}
            <Route
              element={
                <ProtectedRoute>
                  <StudentShell />
                </ProtectedRoute>
              }
            >
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/dashboard/history" element={<StudentPayments />} />
              <Route path="/dashboard/payments" element={<StudentPayments />} />
              <Route path="/dashboard/profile" element={<ProfilePage />} />
              <Route path="/dashboard/support" element={<SupportPage />} />
              <Route path="/dashboard/notifications" element={<StudentNotifications />} />
            </Route>
            
            {/* Admin routes (protected) */}
            <Route 
              path="/admin/*" 
              element={
                <ProtectedRoute>
                  <AdminDashboard />
                </ProtectedRoute>
              } 
            />
            
            {/* Payment routes (protected) */}
            <Route 
              path="/payment/debit-order/setup" 
              element={
                <ProtectedRoute>
                  <DebitOrderSetup />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/payment/debit-order/success" 
              element={
                <ProtectedRoute>
                  <PaymentSuccess />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/payment/:paymentId/payfast" 
              element={
                <ProtectedRoute>
                  <PayFastPayment />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/payment/success" 
              element={
                <ProtectedRoute>
                  <PaymentSuccess />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/payment/cancel" 
              element={
                <ProtectedRoute>
                  <PaymentCancel />
                </ProtectedRoute>
              } 
            />
            
            {/* Catch all - redirect to home */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>
      </Router>
    </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
// Build trigger: Sat Dec 20 00:59:17 SAST 2025
